import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { createNotification } from '@/hooks/useNotifications';
import CardRegistrationModal, { type CardFormData as CardFormDataT } from '@/components/feature/CardRegistrationModal';
import { MATCHING_ENABLED } from '@/config/features';

import { useNotifications } from '@/hooks/useNotifications';

type CardType = 'credit' | 'check';

type MatchRequest = {
  id: string;
  requester_id: string;
  receiver_id: string;
  requester_role: 'patient' | 'caregiver';
  receiver_role: 'patient' | 'caregiver';
  status: 'pending' | 'accepted' | 'rejected' | 'completed' | 'cancelled';
  match_number: string | null;
  start_date: string | null;
  end_date: string | null;
  care_end_date: string | null;
  location: string | null;
  hourly_rate: number | null;
  daily_hours: number | null;
  total_amount: number | null;
  notes: string | null;
  contact_revealed: boolean;
  accepted_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  requester?: { name: string; phone: string } | null;
  receiver?: { name: string; phone: string } | null;
  requester_profile?: { name: string; phone: string; gender: string } | null;
  receiver_profile?: { name: string; phone: string; gender: string } | null;
};

export default function AppMatchingPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const { unreadCount } = useNotifications();
  const [activeTab, setActiveTab] = useState<'received' | 'sent' | 'ongoing' | 'completed'>('received');
  const [matches, setMatches] = useState<MatchRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [revealModal, setRevealModal] = useState<MatchRequest | null>(null);
  const [completionModal, setCompletionModal] = useState<MatchRequest | null>(null);
  const [careEndDate, setCareEndDate] = useState('');
  const [completing, setCompleting] = useState(false);
  const [reviewedMatchIds, setReviewedMatchIds] = useState<Set<string>>(new Set());
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardSaving, setCardSaving] = useState(false);
  const [cardSuccess, setCardSuccess] = useState(false);
  const [hasPaymentCard, setHasPaymentCard] = useState(false);
  const [pendingAcceptMatchId, setPendingAcceptMatchId] = useState<string | null>(null);

  // handleAccept 최신 참조 유지 (useEffect stale closure 방지)
  const handleAcceptRef = useRef(handleAccept);
  handleAcceptRef.current = handleAccept;

  // 완료 모달 열릴 때 기본 날짜를 오늘로 설정
  useEffect(() => {
    if (completionModal) {
      const today = new Date().toISOString().slice(0, 10);
      setCareEndDate(today);
    }
  }, [completionModal]);

  // 카드 등록 상태 불러오기
  useEffect(() => {
    if (!auth.userId) return;
    supabase.from('caregiver_profiles')
      .select('payment_card')
      .eq('user_id', auth.userId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.payment_card) setHasPaymentCard(true);
      });
  }, [auth.userId]);

  useEffect(() => {
    if (!auth.userId) return;
    loadMatches();
  }, [auth.userId, activeTab]);

  // 카드 등록 완료 → 성공 화면에서 확인 누르면 매칭 수락 자동 진행
  useEffect(() => {
    if (!showCardModal && pendingAcceptMatchId) {
      const matchId = pendingAcceptMatchId;
      setPendingAcceptMatchId(null);
      handleAcceptRef.current(matchId);
    }
  }, [showCardModal, pendingAcceptMatchId]);

  async function loadMatches() {
    if (!auth.userId) return;
    setLoading(true);
    setError('');

    try {
      let query = supabase.from('match_requests').select('*');

      if (activeTab === 'received') {
        query = query.eq('receiver_id', auth.userId).in('status', ['pending', 'accepted']);
      } else if (activeTab === 'sent') {
        query = query.eq('requester_id', auth.userId).in('status', ['pending', 'accepted']);
      } else if (activeTab === 'ongoing') {
        query = query.or(`requester_id.eq.${auth.userId},receiver_id.eq.${auth.userId}`).eq('status', 'accepted');
      } else {
        query = query.or(`requester_id.eq.${auth.userId},receiver_id.eq.${auth.userId}`).in('status', ['completed', 'rejected', 'cancelled']);
      }

      const { data, error: queryError } = await query.order('created_at', { ascending: false });

      if (queryError) throw queryError;

      // Fetch profiles for both sides
      const enriched = await Promise.all((data || []).map(async (m: MatchRequest) => {
        const requesterPromise = supabase.from('profiles').select('name, phone').eq('id', m.requester_id).maybeSingle();
        const receiverPromise = supabase.from('profiles').select('name, phone').eq('id', m.receiver_id).maybeSingle();
        const [reqResult, recResult] = await Promise.all([requesterPromise, receiverPromise]);
        return {
          ...m,
          requester: reqResult.data || null,
          receiver: recResult.data || null,
        };
      }));

      setMatches(enriched);

      // 완료 탭일 때: 이미 리뷰를 작성한 매칭 ID 불러오기
      if ((activeTab === 'completed' || activeTab === 'received' || activeTab === 'sent') && auth.userId) {
        const { data: myReviews } = await supabase
          .from('reviews')
          .select('match_id')
          .eq('reviewer_id', auth.userId);
        if (myReviews && myReviews.length > 0) {
          setReviewedMatchIds(new Set(myReviews.map((r) => r.match_id)));
        } else {
          setReviewedMatchIds(new Set());
        }
      }
    } catch (err) {
      console.error('Failed to load matches:', err);
      setError('매칭 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }

  async function sendMatchNotification(match: MatchRequest, status: 'accepted' | 'rejected') {
    try {
      const isRequester = match.requester_id === auth.userId;
      const other = isRequester ? match.receiver : match.requester;
      const me = isRequester ? match.requester : match.receiver;
      const otherRole = isRequester ? match.receiver_role : match.requester_role;
      const myRole = isRequester ? match.requester_role : match.receiver_role;
      const matchNumber = match.match_number || match.id.slice(0, 8);

      // 1. 상대방(요청자 or 수신자)에게 알림
      if (other?.phone) {
        await supabase.functions.invoke('send-notification', {
          body: {
            type: 'match_update',
            to: other.phone,
            matchNumber,
            status,
            role: myRole,
            otherName: me?.name || '상대방',
          },
        });
      }

      // 2. 수락인 경우, 현재 사용자에게도 확인 알림
      if (status === 'accepted' && me?.phone) {
        await supabase.functions.invoke('send-notification', {
          body: {
            type: 'match_update',
            to: me.phone,
            matchNumber,
            status: 'accepted',
            role: otherRole,
            otherName: other?.name || '상대방',
            isSelf: true,
          },
        });
      }
    } catch (err) {
      console.error('Failed to send notification:', err);
    }
  }

  async function handleAccept(matchId: string) {
    if (!MATCHING_ENABLED) {
      setError('현재 매칭 서비스가 일시 중단되었습니다.');
      return;
    }
    setProcessingId(matchId);
    try {
      const match = matches.find((m) => m.id === matchId);
      if (!match) throw new Error('매칭 정보를 찾을 수 없습니다.');

      // 간병인 역할이면 카드 등록 여부 체크
      if (isCaregiverInMatch(match) && !hasPaymentCard) {
        setPendingAcceptMatchId(matchId);
        setShowCardModal(true);
        setProcessingId(null);
        return;
      }

      // Edge Function으로 원자적 수락 처리 (RLS 우회 + 자동취소 포함)
      const { data: acceptResult, error: acceptError } = await supabase.functions.invoke('accept-match', {
        body: { matchId },
      });

      if (acceptError || !acceptResult?.success) {
        throw new Error(acceptError?.message || acceptResult?.error || '매칭 수락에 실패했습니다.');
      }

      const matchNumber = acceptResult.match_number;

      // 알림 발송 (비동기 - 실패해도 매칭 진행)
      sendMatchNotification({ ...match, match_number: matchNumber }, 'accepted');
      // ※ 인앱 알림은 accept-match Edge Function에서 양측 모두에게 생성됨

      // 연락처 공개 모달 띄우기
      const refreshed = await supabase.from('match_requests').select('*').eq('id', matchId).maybeSingle();
      if (refreshed.data) {
        const enriched = {
          ...refreshed.data,
          requester: match.requester,
          receiver: match.receiver,
        };
        setRevealModal(enriched);
      }

      loadMatches();
    } catch (err) {
      console.error('Failed to accept:', err);
      setError('수락 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  }

  async function handleReject(matchId: string) {
    if (!confirm('정말 거절하시겠습니까?')) return;
    setProcessingId(matchId);
    try {
      const match = matches.find((m) => m.id === matchId);

      const { error: updateError } = await supabase
        .from('match_requests')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString(),
        })
        .eq('id', matchId);

      if (updateError) throw updateError;

      // 알림 발송
      if (match) sendMatchNotification(match, 'rejected');

      // 인앱 알림 생성 - 상대방에게 거절 알림
      if (match) {
        const otherUserId = match.requester_id === auth.userId ? match.receiver_id : match.requester_id;
        const myName = match.requester_id === auth.userId ? match.requester?.name : match.receiver?.name;
        createNotification({
          userId: otherUserId,
          type: 'match_rejected',
          title: `매칭이 거절되었습니다`,
          body: `${myName || '상대방'}님이 매칭 요청을 거절했습니다.`,
          relatedId: matchId,
        });
      }

      loadMatches();
    } catch (err) {
      console.error('Failed to reject:', err);
      setError('거절 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  }

  async function handleCancel(matchId: string) {
    if (!confirm('매칭 요청을 취소하시겠습니까?')) return;
    setProcessingId(matchId);
    try {
      const { error: updateError } = await supabase
        .from('match_requests')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString(),
        })
        .eq('id', matchId);

      if (updateError) throw updateError;
      loadMatches();
    } catch (err) {
      console.error('Failed to cancel:', err);
      setError('취소 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  }

  function handleComplete(match: MatchRequest) {
    setCompletionModal(match);
  }

  async function handleCompleteSubmit() {
    if (!completionModal) return;
    if (!careEndDate) {
      alert('간병 완료일자를 입력해주세요.');
      return;
    }

    setCompleting(true);
    try {
      const match = completionModal;

      // 1. 매칭 완료 처리 (care_end_date 포함)
      const { error: updateError } = await supabase
        .from('match_requests')
        .update({
          status: 'completed',
          care_end_date: careEndDate,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', match.id);

      if (updateError) throw updateError;

      // 2. 간병인 프로필 재활성화 (is_active = true)
      const caregiverUserId = match.requester_role === 'caregiver'
        ? match.requester_id
        : match.receiver_id;

      await supabase
        .from('caregiver_profiles')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('user_id', caregiverUserId);

      // 3. 인앱 알림 생성 - 상대방에게 완료 알림 + 리뷰 유도
      const otherUserId = match.requester_id === auth.userId ? match.receiver_id : match.requester_id;
      const otherName = match.requester_id === auth.userId ? match.receiver?.name : match.requester?.name;
      const myName = match.requester_id === auth.userId ? match.requester?.name : match.receiver?.name;

      createNotification({
        userId: otherUserId,
        type: 'match_completed',
        title: `간병이 완료 처리되었습니다`,
        body: `${myName || '상대방'}님이 간병 완료를 요청했습니다. 완료일자: ${careEndDate}. 리뷰를 남겨주세요!`,
        relatedId: match.id,
      });

      // 3-2. 현재 사용자에게도 리뷰 작성 유도 알림
      createNotification({
        userId: auth.userId!,
        type: 'match_completed',
        title: '간병이 완료되었습니다',
        body: `${otherName || '상대방'}님과의 간병이 완료 처리되었습니다. 리뷰를 작성해보세요!`,
        relatedId: match.id,
      });

      // 4. 모달 닫고 목록 새로고침
      setCompletionModal(null);
      loadMatches();
    } catch (err) {
      console.error('Failed to complete:', err);
      setError('완료 처리 중 오류가 발생했습니다.');
    } finally {
      setCompleting(false);
    }
  }

  function isMyRequest(m: MatchRequest) {
    return m.requester_id === auth.userId;
  }

  function getOtherPerson(m: MatchRequest) {
    const isRequester = m.requester_id === auth.userId;
    return {
      profile: isRequester ? m.receiver : m.requester,
      role: isRequester ? m.receiver_role : m.requester_role,
    };
  }

  // 현재 사용자가 이 매칭에서 간병인 역할인지 확인
  async function handleCardRegisterForMatch(cardType: CardType, formData: CardFormDataT) {
    setCardSaving(true);
    try {
      const last4 = formData.cardNumber4;
      const paymentCard = {
        card_type: cardType,
        last4,
        updated_at: new Date().toISOString(),
      };
      if (auth.userId) {
        await supabase.from('caregiver_profiles')
          .upsert({ user_id: auth.userId, payment_card: paymentCard, updated_at: new Date().toISOString() });
      }
      setHasPaymentCard(true);
      setCardSuccess(true);
      setCardSaving(false);
    } catch (err) {
      console.error('Failed to save card:', err);
      setCardSaving(false);
    }
  }

  function isCaregiverInMatch(m: MatchRequest): boolean {
    if (m.requester_id === auth.userId) {
      return m.requester_role === 'caregiver';
    }
    if (m.receiver_id === auth.userId) {
      return m.receiver_role === 'caregiver';
    }
    return false;
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: 'bg-amber-50', text: 'text-amber-600', label: '대기중' },
      accepted: { bg: 'bg-green-50', text: 'text-green-600', label: '수락됨' },
      rejected: { bg: 'bg-red-50', text: 'text-red-500', label: '거절됨' },
      completed: { bg: 'bg-blue-50', text: 'text-blue-600', label: '완료' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: '취소됨' },
    };
    const s = styles[status] || styles.pending;
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.bg} ${s.text}`}>{s.label}</span>;
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  if (auth.loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate('/app')} className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-gray-900">매칭 관리</h1>
            <p className="text-xs text-gray-400">매칭 요청을 확인하고 관리하세요</p>
          </div>
          <button
            onClick={() => navigate('/app/notifications')}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors relative"
          >
            <i className="ri-notification-3-line text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* 탭 */}
        <div className="flex bg-gray-100 rounded-full p-1 mb-5">
          {[
            { key: 'received' as const, label: '받은 요청' },
            { key: 'sent' as const, label: '보낸 요청' },
            { key: 'ongoing' as const, label: '진행 중' },
            { key: 'completed' as const, label: '완료/취소' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 에러 */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">{error}</div>
        )}

        {/* 로딩 */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-line text-2xl text-gray-300" />
            </div>
            <p className="text-sm text-gray-400 font-medium">
              {activeTab === 'received' ? '받은 매칭 요청이 없습니다' :
               activeTab === 'sent' ? '보낸 매칭 요청이 없습니다' :
               activeTab === 'ongoing' ? '진행 중인 간병이 없습니다' : '완료된 매칭이 없습니다'}
            </p>
            <p className="text-xs text-gray-300 mt-1">
              {activeTab === 'received' ? '환자/간병인이 매칭 요청을 보내면 여기에 표시됩니다' :
               activeTab === 'sent' ? '간병인 검색에서 매칭을 신청해보세요' :
               activeTab === 'ongoing' ? '수락된 매칭이 여기에 표시됩니다' : ''}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((m) => {
              const other = getOtherPerson(m);
              return (
                <div key={m.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  {/* 상단 정보 */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          other.role === 'caregiver' ? 'bg-[#E3F2FD]' : 'bg-[#E8F5E9]'
                        }`}>
                          <i className={`${other.role === 'caregiver' ? 'ri-nurse-line text-[#1976D2]' : 'ri-hospital-line text-[#2E7D6B]'} text-lg`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-gray-900">
                              {other.profile?.name || '알 수 없음'}
                            </h3>
                            {getStatusBadge(m.status)}
                          </div>
                          <p className="text-xs text-gray-400">
                            {other.role === 'caregiver' ? '간병인' : '환자/보호자'}
                            {isMyRequest(m) ? '에게 보낸 요청' : '님의 요청'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 매칭 정보 */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {m.hourly_rate && (
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <i className="ri-money-dollar-circle-line text-gray-400" />
                          {m.hourly_rate.toLocaleString()}원/시간
                        </div>
                      )}
                      {m.location && (
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <i className="ri-map-pin-line text-gray-400" />
                          {m.location.length > 12 ? m.location.slice(0, 12) + '...' : m.location}
                        </div>
                      )}
                      {m.start_date && (
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <i className="ri-calendar-line text-gray-400" />
                          {formatDate(m.start_date)} ~ {formatDate(m.end_date)}
                        </div>
                      )}
                      {m.daily_hours && (
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <i className="ri-time-line text-gray-400" />
                          하루 {m.daily_hours}시간
                        </div>
                      )}
                    </div>

                    {m.notes && (
                      <div className="bg-gray-50 rounded-xl p-3 mb-2">
                        <p className="text-xs text-gray-500 leading-relaxed">{m.notes}</p>
                      </div>
                    )}

                    {/* 연락처 공개됨 */}
                    {m.contact_revealed && (
                      <div className="bg-green-50 rounded-xl p-3 mb-2 space-y-1.5">
                        {m.match_number && (
                          <div className="flex items-center gap-1.5">
                            <i className="ri-hashtag text-green-600 text-xs" />
                            <span className="text-xs font-bold text-green-700">매칭번호: {m.match_number}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <i className="ri-phone-line text-green-600" />
                          <div>
                            <p className="text-xs font-bold text-green-700">연락처가 공개되었습니다</p>
                            <p className="text-xs text-green-600">
                              {other.profile?.phone || '연락처 없음'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 간병비 정산 가이드 (수락된 매칭에 표시) */}
                    {m.status === 'accepted' && m.contact_revealed && (() => {
                      const hourlyRate = m.hourly_rate || 0;
                      const dailyHours = m.daily_hours || 0;
                      const dailyIncome = hourlyRate * dailyHours;
                      let totalDays = 0;
                      if (m.start_date && m.end_date) {
                        const start = new Date(m.start_date);
                        const end = new Date(m.end_date);
                        totalDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
                      }
                      const estimatedTotal = dailyIncome * totalDays;
                      const serviceFee = Math.round(estimatedTotal * 0.05);
                      const netIncome = estimatedTotal - serviceFee;

                      return (
                        <div className="bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl p-3 mb-2">
                          <p className="text-[10px] font-bold text-[#2E7D6B] uppercase tracking-wider mb-2 flex items-center gap-1">
                            <i className="ri-bank-line text-xs" /> 간병비 정산 가이드
                          </p>
                          <div className="space-y-1 text-[10px]">
                            <div className="flex justify-between">
                              <span className="text-gray-500">시급</span>
                              <span className="font-bold text-gray-800">{hourlyRate.toLocaleString()}원</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">하루 {dailyHours}시간</span>
                              <span className="font-bold text-gray-800">{dailyIncome.toLocaleString()}원</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">총 {totalDays}일</span>
                              <span className="font-bold text-gray-800">{estimatedTotal.toLocaleString()}원</span>
                            </div>
                            <hr className="border-[#C8E6C9] my-1" />
                            <div className="flex justify-between">
                              <span className="text-red-500">플랫폼 수수료 (5%)</span>
                              <span className="font-bold text-red-500">-{serviceFee.toLocaleString()}원</span>
                            </div>
                            <div className="flex justify-between text-xs bg-white rounded-lg p-1.5 -mx-0.5">
                              <span className="font-extrabold text-[#2E7D6B]">간병인 실수령</span>
                              <span className="font-extrabold text-[#2E7D6B]">{netIncome.toLocaleString()}원</span>
                            </div>
                          </div>
                          <div className="mt-2 p-2 bg-white/70 rounded-lg border border-[#C8E6C9]">
                            <p className="text-[9px] text-gray-500 leading-relaxed">
                              <i className="ri-information-line text-[#2E7D6B] mr-1" />
                              수수료는 간병 완료 후 <strong>7영업일 이내</strong> 국민은행 123456-78-901234 (주)케어링크로 입금
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 간병인 전용: 간병중 상태 배너 */}
                    {m.status === 'accepted' && isCaregiverInMatch(m) && (
                      <div className="bg-[#E3F2FD] border border-[#90CAF9] rounded-xl p-3 mb-2">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-6 h-6 rounded-full bg-[#1976D2] flex items-center justify-center">
                            <i className="ri-heart-pulse-line text-white text-xs" />
                          </div>
                          <div>
                            <p className="text-xs font-extrabold text-[#1565C0]">현재 간병중</p>
                            <p className="text-[10px] text-[#1976D2]">
                              {m.start_date ? `${formatDate(m.start_date)} ~ ${formatDate(m.end_date)}` : '간병 진행 중'}
                            </p>
                          </div>
                        </div>
                        <p className="text-[10px] text-[#1565C0] leading-relaxed">
                          <i className="ri-information-line mr-0.5" />
                          간병이 종료되면 반드시 <strong>간병 완료 요청</strong> 버튼을 눌러 완료 처리해주세요.
                          완료 전까지는 새로운 매칭을 받을 수 없습니다.
                        </p>
                      </div>
                    )}

                    {/* 액션 버튼 */}
                    <div className="flex gap-2 border-t border-gray-100 pt-3 mt-1">
                      {m.status === 'pending' && !isMyRequest(m) && (
                        <>
                          <button
                            onClick={() => handleAccept(m.id)}
                            disabled={!MATCHING_ENABLED || processingId === m.id}
                            className="flex-1 py-2.5 rounded-xl text-xs font-bold text-white cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
                            style={{ backgroundColor: '#2E7D6B' }}
                          >
                            {!MATCHING_ENABLED ? '매칭 서비스 준비중' : processingId === m.id ? '처리중...' : '수락하기'}
                          </button>
                          <button
                            onClick={() => handleReject(m.id)}
                            disabled={processingId === m.id}
                            className="px-4 py-2.5 rounded-xl text-xs font-bold text-red-500 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors disabled:opacity-50"
                          >
                            거절
                          </button>
                        </>
                      )}
                      {m.status === 'pending' && isMyRequest(m) && (
                        <button
                          onClick={() => handleCancel(m.id)}
                          disabled={processingId === m.id}
                          className="w-full py-2.5 rounded-xl text-xs font-bold text-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                          요청 취소하기
                        </button>
                      )}
                      {/* 간병인 전용: 간병 완료 요청 + 채팅 */}
                      {m.status === 'accepted' && isCaregiverInMatch(m) && (
                        <>
                          <button
                            onClick={() => handleComplete(m)}
                            disabled={processingId === m.id}
                            className="flex-1 py-3 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1.5 whitespace-nowrap"
                            style={{ backgroundColor: '#1976D2' }}
                          >
                            <i className="ri-check-double-line text-base" />
                            간병 완료 요청
                          </button>
                          <button
                            onClick={() => navigate(`/app/chat?match_id=${m.id}`)}
                            className="flex-1 py-3 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5 whitespace-nowrap"
                            style={{ backgroundColor: '#2E7D6B' }}
                          >
                            <i className="ri-chat-3-line text-base" />
                            채팅
                          </button>
                        </>
                      )}
                      {/* 환자/보호자: 채팅이 메인 액션 */}
                      {m.status === 'accepted' && !isCaregiverInMatch(m) && (
                        <button
                          onClick={() => navigate(`/app/chat?match_id=${m.id}`)}
                          className="w-full py-3 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                          style={{ backgroundColor: '#2E7D6B' }}
                        >
                          <i className="ri-chat-3-line text-base" />
                          채팅하기
                        </button>
                      )}
                      {m.status === 'completed' && !reviewedMatchIds.has(m.id) && (
                        <button
                          onClick={() => navigate(`/app/review/write?match_id=${m.id}`)}
                          className="w-full py-2.5 rounded-xl text-xs font-bold text-[#F57C00] bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors"
                        >
                          <i className="ri-star-line mr-1" />
                          리뷰 작성하기
                        </button>
                      )}
                      {m.status === 'completed' && reviewedMatchIds.has(m.id) && (
                        <div className="w-full py-2.5 rounded-xl text-xs font-bold text-[#2E7D6B] bg-[#E8F5E9] text-center">
                          <i className="ri-check-double-line mr-1" />
                          리뷰 작성 완료
                        </div>
                      )}
                    </div>

                    <p className="text-[10px] text-gray-300 mt-2 text-right">
                      {formatDate(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 연락처 공개 모달 */}
        {revealModal && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onClick={() => setRevealModal(null)}>
            <div
              className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl p-6 animate-slide-up max-h-[90vh] md:max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-double-line text-2xl text-[#2E7D6B]" />
              </div>
              <h2 className="text-lg font-extrabold text-gray-900 text-center mb-2">매칭이 성사되었습니다!</h2>
              {revealModal.match_number && (
                <p className="text-sm font-extrabold text-[#2E7D6B] text-center mb-4">
                  매칭번호: {revealModal.match_number}
                </p>
              )}
              <p className="text-sm text-gray-500 text-center mb-5">
                상대방의 연락처가 공개되었습니다.<br />원활한 간병 서비스를 위해 연락해보세요.
              </p>

              <div className="bg-gray-50 rounded-2xl p-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                    getOtherPerson(revealModal).role === 'caregiver' ? 'bg-[#E3F2FD]' : 'bg-[#E8F5E9]'
                  }`}>
                    <i className={`text-xl ${getOtherPerson(revealModal).role === 'caregiver' ? 'ri-nurse-line text-[#1976D2]' : 'ri-hospital-line text-[#2E7D6B]'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{getOtherPerson(revealModal).profile?.name}</p>
                    <p className="text-xs text-gray-500">
                      {getOtherPerson(revealModal).role === 'caregiver' ? '간병인' : '환자/보호자'}
                    </p>
                    <p className="text-sm font-bold text-[#2E7D6B] mt-1">
                      <i className="ri-phone-line mr-1" />
                      {getOtherPerson(revealModal).profile?.phone}
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setRevealModal(null);
                  navigate(`/app/chat?match_id=${revealModal.id}`);
                }}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5"
                style={{ backgroundColor: '#2E7D6B' }}
              >
                <i className="ri-chat-3-line text-base" />
                바로 채팅하기
              </button>
              <button
                onClick={() => setRevealModal(null)}
                className="w-full py-3 rounded-xl text-sm font-bold text-gray-400 cursor-pointer hover:text-gray-600 hover:bg-gray-50 transition-colors mt-2"
              >
                확인했습니다
              </button>
            </div>
          </div>
        )}

        {/* 간병 완료 요청 모달 */}
        {completionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !completing && setCompletionModal(null)}>
            <div
              className="bg-white w-full max-w-md rounded-2xl overflow-hidden animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 - 강조 */}
              <div className="bg-[#1976D2] px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <i className="ri-calendar-check-line text-xl text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-extrabold text-white">간병 완료 처리</h2>
                    <p className="text-[11px] text-white/70">모든 정보를 정확히 입력해주세요</p>
                  </div>
                </div>
              </div>

              {/* 모달 본문 */}
              <div className="p-5 space-y-4">
                {/* ⚠️ 중요 안내 */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5">
                  <div className="flex items-start gap-2">
                    <i className="ri-error-warning-line text-amber-500 text-sm mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-extrabold text-amber-700 mb-1">반드시 확인해주세요</p>
                      <ul className="text-[11px] text-amber-600 space-y-1">
                        <li>• 간병이 <strong className="text-amber-800">실제로 종료된 날짜</strong>를 정확히 입력하세요.</li>
                        <li>• 완료 처리 후에는 <strong className="text-amber-800">되돌릴 수 없습니다.</strong></li>
                        <li>• 완료 시 새로운 매칭을 받을 수 있는 상태로 전환됩니다.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* 매칭 정보 요약 */}
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">매칭 정보</p>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">상대방</span>
                      <span className="font-bold text-gray-900">
                        {getOtherPerson(completionModal).profile?.name || '알 수 없음'}
                        <span className="text-[10px] text-gray-400 ml-1">
                          ({getOtherPerson(completionModal).role === 'caregiver' ? '간병인' : '환자/보호자'})
                        </span>
                      </span>
                    </div>
                    {completionModal.match_number && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">매칭번호</span>
                        <span className="font-bold text-gray-900">{completionModal.match_number}</span>
                      </div>
                    )}
                    {completionModal.start_date && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">간병 기간</span>
                        <span className="font-bold text-gray-900">
                          {formatDate(completionModal.start_date)} ~ {formatDate(completionModal.end_date)}
                        </span>
                      </div>
                    )}
                    {completionModal.location && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">위치</span>
                        <span className="font-bold text-gray-900">{completionModal.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 수수료 입금 안내 */}
                {(() => {
                  const hourlyRate = completionModal.hourly_rate || 0;
                  const dailyHours = completionModal.daily_hours || 0;
                  const dailyIncome = hourlyRate * dailyHours;
                  let totalDays = 0;
                  if (completionModal.start_date && completionModal.end_date) {
                    const start = new Date(completionModal.start_date);
                    const end = new Date(completionModal.end_date);
                    totalDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
                  }
                  const estimatedTotal = dailyIncome * totalDays;
                  const serviceFee = Math.round(estimatedTotal * 0.05);
                  const netIncome = estimatedTotal - serviceFee;

                  return (
                    <div className="bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl p-3.5">
                      <p className="text-[10px] font-bold text-[#2E7D6B] uppercase tracking-wider mb-2 flex items-center gap-1">
                        <i className="ri-bank-line text-xs" /> 수수료 입금 안내
                      </p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">시급</span>
                          <span className="font-bold text-gray-900">{hourlyRate.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">하루 근무시간</span>
                          <span className="font-bold text-gray-900">{dailyHours}시간</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">일당 (추정)</span>
                          <span className="font-bold text-gray-900">{dailyIncome.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">총 간병일</span>
                          <span className="font-bold text-gray-900">{totalDays}일</span>
                        </div>
                        <hr className="border-[#C8E6C9] my-1" />
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">총 간병비 (추정)</span>
                          <span className="font-bold text-gray-900">{estimatedTotal.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-red-500">플랫폼 수수료 (5%)</span>
                          <span className="font-bold text-red-500">-{serviceFee.toLocaleString()}원</span>
                        </div>
                        <hr className="border-[#C8E6C9] my-1" />
                        <div className="flex justify-between text-sm bg-white rounded-lg p-2 -mx-1">
                          <span className="font-extrabold text-[#2E7D6B]">실수령 예상액</span>
                          <span className="font-extrabold text-[#2E7D6B]">{netIncome.toLocaleString()}원</span>
                        </div>
                      </div>
                      <div className="mt-3 bg-white/70 rounded-lg p-2.5 border border-[#C8E6C9]">
                        <p className="text-[10px] text-gray-600 leading-relaxed">
                          <i className="ri-information-line text-[#2E7D6B] mr-1" />
                          수수료는 간병 완료 후 <strong className="text-gray-800">7영업일 이내</strong>에 아래 계좌로 입금해주시면 됩니다. 입금 확인 후 정산이 완료됩니다.
                        </p>
                        <div className="mt-2 flex items-center gap-2 bg-white rounded-lg p-2 border border-gray-200">
                          <i className="ri-bank-card-line text-gray-400 text-sm" />
                          <div className="text-[10px]">
                            <p className="text-gray-400">입금 계좌</p>
                            <p className="font-bold text-gray-800">국민은행 123456-78-901234 (주)케어링크</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 완료일자 입력 */}
                <div>
                  <label className="block text-xs font-extrabold text-gray-700 mb-2">
                    <i className="ri-calendar-line mr-1 text-red-400" />
                    간병 완료일자 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={careEndDate}
                    onChange={(e) => setCareEndDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-sm text-gray-900 bg-gray-50 focus:outline-none focus:border-[#1976D2] focus:bg-white transition-colors"
                  />
                  <p className="text-[10px] text-red-400 mt-1.5 flex items-center gap-1">
                    <i className="ri-arrow-up-line" />
                    실제 간병이 종료된 날짜를 반드시 정확히 선택해주세요
                  </p>
                </div>

                {/* 버튼 */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => !completing && setCompletionModal(null)}
                    disabled={completing}
                    className="flex-1 py-3 rounded-xl text-sm font-bold text-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleCompleteSubmit}
                    disabled={completing || !careEndDate}
                    className="flex-[2] py-3 rounded-xl text-sm font-bold text-white cursor-pointer transition-all disabled:opacity-50 whitespace-nowrap"
                    style={{
                      backgroundColor: completing || !careEndDate ? '#B0BEC5' : '#1976D2',
                    }}
                  >
                    {completing ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        처리중...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-1.5">
                        <i className="ri-check-double-line" />
                        간병 완료 처리하기
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 카드 등록 모달 (매칭 수락 시 카드 미등록 상태면 노출) */}
        <CardRegistrationModal
          isOpen={showCardModal}
          onClose={() => {
            if (!cardSaving) {
              setShowCardModal(false);
              if (!cardSuccess) {
                setPendingAcceptMatchId(null);
              }
              setCardSuccess(false);
            }
          }}
          onRegister={handleCardRegisterForMatch}
          saving={cardSaving}
          success={cardSuccess}
        />

        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
          @keyframes scale-in {
            from { transform: scale(0.95); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-scale-in {
            animation: scale-in 0.2s ease-out;
          }
        `}</style>
      </div>
    </AppLayout>
  );
}